/* eslint-disable @typescript-eslint/promise-function-async */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { RootStore } from "../stores";
import type { EngineClient } from "../services/engineClient";
import type {
  AuthenticationMethodDescription,
  Credential,
  InputData,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { frameOAuthStore } from "./frameOAuthStore";
import * as T from "../stores/automation/mergers/trigger";
import * as A from "../stores/automation/mergers/actions";
import * as R from "remeda";
import type { ParagonCredentials } from "../services/paragon";
import { saveCredential } from "../services/paragon";
import { REGEX_EMAIL } from "../utils/constants";

const getNumberCredsBySolution = (
  creds: Credential[],
  integrationId: string,
  solutionId: string,
): number => {
  return creds.filter((cred: Credential) => {
    return (
      cred.solutionId === solutionId &&
      cred.authenticationMethodReference?.integrationId === integrationId &&
      !REGEX_EMAIL.test(cred.label)
    );
  }).length;
};

const getCredLabel = (
  store: RootStore,
  integrationId: string,
  solutionId: string,
): string => {
  const {
    integrations,
    credentials,
    config: { userPreferences },
  } = store;
  const integration = integrations.list.filter(
    (item: IntegrationDescription) => {
      return item.integrationId === integrationId;
    },
  )[0];
  const numberOfIntegrations = getNumberCredsBySolution(
    credentials.list,
    integrationId,
    solutionId,
  );
  const fullName =
    (userPreferences.full_name?.first_name ?? "") +
    " " +
    (userPreferences.full_name?.last_name ?? "");

  return integration !== undefined
    ? `${integration.label} ${fullName} Credential # ${
        numberOfIntegrations + 1
      }`
    : `${fullName} Credential # ${numberOfIntegrations + 1}`;
};

export const authDispatcher = (
  { integrationId, authMethod, target }: AuthAttempt,
  inputList: InputData[],
  store: RootStore,
  engineClient: EngineClient,
  actionGroupType?: "actions" | "loop" | "repeat" | "branches",
  paragonUserId?: string,
  paragonUserProviderId?: string,
): Promise<"started" | "success"> => {
  const {
    credentials,
    automations: { candidate },
    config: {
      userPreferences: { email },
      accountId,
    },
  } = store;

  const solutionId = candidate.self.solutionId;

  switch (authMethod.type.oneofKind) {
    case "oauth": {
      const activeAttempt = frameOAuthStore.getActiveAttempt();
      if (activeAttempt !== undefined) {
        // show already opened window.
        activeAttempt.oauthWindow.focus();
        throw new Error("One oauth attempt haven't done");
      }

      const authUrl = authMethod.type.oauth.authenticateBrowserUrl;
      // We need to open window before request
      const oauthWindow = window.open(
        undefined,
        "OAuth Authorization",
        "popup=yes,left=100,top=100,width=480,height=640",
      );

      return engineClient
        .createOauthCredential({
          solutionId,
          integrationId,
          authenticationMethodId: authMethod.authenticationMethodId,
        })
        .then((pendingCred) => {
          if (oauthWindow !== null) {
            oauthWindow.location = `${authUrl}?credentialId=${pendingCred.credentialId}`;
            oauthWindow.focus();
            frameOAuthStore.startOAuth(
              oauthWindow,
              solutionId,
              target,
              integrationId,
              pendingCred.credentialId,
              store,
              engineClient,
            );
            return "started";
          } else {
            throw new Error("Oauth window didn't open");
          }
        });
    }
    case "smartSuite": {
      return engineClient
        .createSmartSuiteCredential({
          solutionId,
          authenticationMethodReference: {
            integrationId,
            authenticationMethodId: authMethod.authenticationMethodId,
          },
        })
        .then(({ credential }) => {
          credentials.appendItemIfNew(credential!);

          if (target.type === "trigger") {
            if (candidate.self.trigger.hash === target.hash) {
              candidate.setTrigger(
                T.mergeCredentialId(credential!.credentialId),
              );
            }
          } else if (target.type === "action") {
            candidate.setActions(
              [A.mergeCredentialId(target.hash, credential!.credentialId)],
              actionGroupType,
            );
          }
          return "success";
        });
    }
    case "inputs": {
      return engineClient
        .createInputsCredential({
          solutionId,
          authenticationMethodReference: {
            integrationId,
            authenticationMethodId: authMethod.authenticationMethodId,
          },
          inputs: inputList,
        })
        .then(({ credential }) => {
          const { trigger } = candidate.self;
          credentials.appendItemIfNew(credential!);

          if (target.type === "trigger") {
            if (trigger.hash === target.hash) {
              candidate.setTrigger(
                T.mergeCredentialId(credential!.credentialId),
              );
            }
          } else if (target.type === "action") {
            candidate.setActions(
              [A.mergeCredentialId(target.hash, credential!.credentialId)],
              actionGroupType,
            );
          }
          return "success";
        });
    }
    case "paragonAuth": {
      if (store.config.solutionId !== solutionId) {
        throw new Error("SolutionId was changed");
      }
      if (paragonUserId === undefined) {
        throw new Error("Paragon UserId not found");
      }

      const authUrl = authMethod.type.paragonAuth.authenticateBrowserUrl;
      const isEmail = REGEX_EMAIL.test(paragonUserProviderId ?? "");
      const paragonCredential: ParagonCredentials = {
        integrationId,
        accountId,
        solutionId,
        userId: paragonUserId ?? "",
        email: email ?? "",
        label: isEmail
          ? (paragonUserProviderId ?? "")
          : getCredLabel(store, integrationId, solutionId),
      };

      return saveCredential(authUrl, paragonCredential)
        .then((pendingCred) => {
          return Promise.all([
            engineClient.getAllCredentialList({
              solutionId,
              pageToken: "",
              pageSize: store.credentials.pageSize,
            }),
            { pendingCred },
          ]);
        })
        .then(([nextCredentialList, { pendingCred }]) => {
          const { credentials } = store;

          if (R.isTruthy(pendingCred)) {
            if (target.type === "trigger") {
              if (candidate.self.trigger.hash === target.hash) {
                candidate.setTrigger(
                  T.mergeCredentialId(pendingCred.credentialId),
                );
              }
            } else if (target.type === "action") {
              candidate.setActions(
                [A.mergeCredentialId(target.hash, pendingCred.credentialId)],
                actionGroupType,
              );
            }
          }

          credentials.setList(nextCredentialList);
          return "success";
        });
    }
    case "dynamicInputs":
      return engineClient
        .createInputsCredential({
          solutionId,
          authenticationMethodReference: {
            integrationId,
            authenticationMethodId: authMethod.authenticationMethodId,
          },
          inputs: inputList,
        })
        .then(({ credential }) => {
          const { trigger } = candidate.self;
          credentials.appendItemIfNew(credential!);

          if (target.type === "trigger") {
            if (trigger.hash === target.hash) {
              candidate.setTrigger(
                T.mergeCredentialId(credential!.credentialId),
              );
            }
          } else if (target.type === "action") {
            candidate.setActions(
              [A.mergeCredentialId(target.hash, credential!.credentialId)],
              actionGroupType,
            );
          }
          return "success";
        });
    default:
      throw new Error("Unknown auth method");
  }
};

export interface AuthAttempt {
  integrationId: string;
  authMethod: AuthenticationMethodDescription;
  target: AuthTarget;
  initialValues?: { [key: string]: string };
}

export type AuthTarget =
  | { type: "trigger"; hash: string }
  | { type: "action"; hash: string };
